import {
  cleJeuSchema,
  type FinJeu,
  type IndiceJeu,
  type JaugeJeu,
  type OrigineJeu,
  type QuoiJournalJeu,
  type SceneJeuResume,
} from "./contract";

/**
 * Les textes et les calculs purs du module Livres-jeux (#276) : libelles, cle proposee depuis un
 * titre, ordre des scenes, structure d'une scene en clair. Aucune horloge implicite : l'instant
 * courant est passe en argument (les dates en clair sont dans `dates-en-clair.ts`).
 */

export const LIBELLE_ORIGINE: Readonly<Record<OrigineJeu, string>> = {
  nicolas: "Nicolas",
  claude: "Claude",
  "les-deux": "Nicolas et Claude",
};

export const LIBELLE_FIN: Readonly<Record<FinJeu, string>> = {
  reussie: "fin réussie",
  ratee: "fin ratée",
};

export const LIBELLE_QUOI: Readonly<Record<QuoiJournalJeu, string>> = {
  livre_cree: "a créé le livre",
  trame: "a écrit la trame",
  reglages: "a changé les réglages",
  scene_creee: "a ajouté une scène",
  scene_supprimee: "a supprimé une scène",
  ordre: "a changé l'ordre des scènes",
  texte: "a écrit le texte",
  structure: "a changé la structure",
  import: "a importé le livre en YAML",
  publication: "a publié vers l'appli",
};

/** Le titre d'une scene en clair : « 3. L'entrée », ou sa cle quand elle n'a pas de titre. */
export function titreDeScene(scene: {
  readonly rang: number;
  readonly cle: string;
  readonly titre: string | null;
}): string {
  return `${scene.rang}. ${scene.titre?.trim() ? scene.titre : scene.cle}`;
}

/**
 * La cle proposee depuis un titre : minuscules sans accents, chiffres, et un tiret a la place
 * de tout le reste, 60 caracteres au plus. `cleJeuSchema` a le dernier mot.
 */
export function cleDepuisTitre(titre: string): string {
  const cle = titre
    .toLocaleLowerCase("fr")
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .replaceAll("œ", "oe")
    .replaceAll("æ", "ae")
    .replaceAll("ß", "ss")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return cle.slice(0, 60).replace(/-+$/, "");
}

/** Pourquoi une cle ne passe pas, ou `null` quand le contrat l'accepte. */
export function refusDeCle(cle: string): string | null {
  if (cle === "") return "L'identifiant est vide.";
  if (cleJeuSchema.safeParse(cle).success) return null;
  return "Minuscules, chiffres et tirets seulement, une lettre ou un chiffre en premier, 60 caractères au plus.";
}

/** Les identifiants des scenes dans l'ordre ou la scene `id` monte (-1) ou descend (+1). */
export function deplacer(
  scenes: readonly Pick<SceneJeuResume, "id" | "rang">[],
  id: number,
  sens: -1 | 1,
): number[] | null {
  const ordre = [...scenes].sort((a, b) => a.rang - b.rang).map((scene) => scene.id);
  const rang = ordre.indexOf(id);
  const cible = rang + sens;
  if (rang === -1 || cible < 0 || cible >= ordre.length) return null;
  [ordre[rang], ordre[cible]] = [ordre[cible] as number, ordre[rang] as number];
  return ordre;
}

/** Un nombre et son nom, accorde : « 1 scène », « 3 scènes », « aucune scène ». */
export function compterJeu(nombre: number, un: string, plusieurs: string, aucun?: string): string {
  if (nombre === 0 && aucun !== undefined) return aucun;
  return `${nombre} ${nombre > 1 ? plusieurs : un}`;
}

/** Les cles que les choix d'une scene visent et que le livre n'a pas (encore). */
export function ciblesInconnues(
  scene: Pick<SceneJeuResume, "choix">,
  cles: ReadonlySet<string>,
): string[] {
  return [...new Set(scene.choix.flatMap((choix) => choix.cibles))].filter((cle) => !cles.has(cle));
}

/** Le pense-bete d'une scene : ce que ses choix peuvent nommer. */
export function penseBete(livre: {
  readonly scenes: readonly { readonly cle: string }[];
  readonly indices: readonly IndiceJeu[];
  readonly jauges: readonly Pick<JaugeJeu, "id" | "libelle" | "max">[];
}): { scenes: string; indices: string; jauges: string } {
  return {
    scenes: livre.scenes.map((scene) => scene.cle).join(", ") || "aucune",
    indices: livre.indices.map((indice) => indice.id).join(", ") || "aucun",
    jauges: livre.jauges.map((jauge) => `${jauge.id} (0 à ${jauge.max})`).join(", ") || "aucune",
  };
}

/** Un nom de fichier pour un telechargement : `manoir.pdf`, `manoir.yaml`. */
export function nomDeFichier(cle: string, extension: "pdf" | "yaml"): string {
  return `${cle}.${extension}`;
}

/** Les octets d'un PDF rendu en base64 par la console. */
export function octetsDuBase64(base64: string): Uint8Array<ArrayBuffer> {
  const binaire = atob(base64);
  const octets = new Uint8Array(binaire.length);
  for (let i = 0; i < binaire.length; i += 1) octets[i] = binaire.charCodeAt(i);
  return octets;
}
